import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'
import type { WinningNumberGroup } from './TicketDetails'

/** Render a winning number, coloring X characters with the accent color */
function renderWinningNumber(num: string) {
  if (!num.includes('X')) return num
  return (
    <>
      {num.split('').map((ch, i) =>
        ch === 'X' ? (
          <span
            key={i}
            style={{ color: 'var(--set-lottery-group-header-text)' }}
          >
            {ch}
          </span>
        ) : (
          <span key={i}>{ch}</span>
        ),
      )}
    </>
  )
}

export interface WinningNumbersCollapsibleProps {
  winningNumbers: WinningNumberGroup[]
  /** Extra top margin class, e.g. 'mt-4' for card view or 'mt-3' for table view */
  className?: string
}

const WinningNumbersCollapsible: React.FC<WinningNumbersCollapsibleProps> = ({
  winningNumbers,
  className = 'mt-4',
}) => {
  const [expanded, setExpanded] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const { t } = useTranslation()

  if (!winningNumbers || winningNumbers.length === 0) return null

  // Split groups into standalone (no row) and row-grouped (side by side)
  const standalone = winningNumbers.filter((g) => g.row === undefined)
  const rowMap = new Map<number, WinningNumberGroup[]>()
  winningNumbers.forEach((g) => {
    if (g.row !== undefined) {
      const list = rowMap.get(g.row) ?? []
      list.push(g)
      rowMap.set(g.row, list)
    }
  })

  const renderGroup = (group: WinningNumberGroup) => (
    <div
      key={group.labelKey}
      className="rounded-lg border border-[var(--reward-result-group-border)] bg-[var(--bet-detail-card-bg)] p-2.5 sm:p-3"
    >
      <div className="py-2.5 px-6 text-center text-sm font-semibold rounded-lg bg-[var(--set-lottery-group-header-bg)] text-[var(--set-lottery-group-header-text)] mb-4">
        {t(group.labelKey, group.label)}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-y-3">
        {group.numbers.map((num) => (
          <div
            key={num}
            className="w-1/5 sm:w-[10%] text-center py-1.5 px-1 text-xs sm:text-sm font-medium rounded-md text-[var(--bet-detail-row-label)]"
          >
            {renderWinningNumber(num)}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className={className}>
      {/* Toggle button — React state-driven hover replaces querySelector DOM manipulation */}
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={
          expanded
            ? t('setLottery.hideNumbers', 'Hide Numbers')
            : t(
                'setLottery.showAllOtherWinning',
                'Show all other winning numbers',
              )
        }
        className="w-full rounded-lg px-4 py-2.5 transition-colors cursor-pointer relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setExpanded(!expanded)}
      >
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.5"
            y="0.5"
            rx="8"
            ry="8"
            fill="none"
            strokeWidth="1"
            strokeDasharray="6 6"
            className="transition-[stroke]"
            style={{
              width: 'calc(100% - 1px)',
              height: 'calc(100% - 1px)',
              stroke: isHovered
                ? 'var(--reward-result-collapsible-hover)'
                : 'var(--reward-result-collapsible-border)',
            }}
          />
        </svg>
        <span
          className="w-full flex items-center justify-center gap-2 py-1 text-xs transition-colors"
          style={{
            color: isHovered
              ? 'var(--reward-result-collapsible-hover)'
              : 'var(--reward-result-collapsible-text)',
          }}
        >
          {expanded ? (
            <>
              <FiChevronUp className="w-4 h-4" />
              {t('setLottery.hideNumbers', 'Hide Numbers')}
            </>
          ) : (
            <>
              <FiChevronDown className="w-4 h-4" />
              {t(
                'setLottery.showAllOtherWinning',
                'Show all other winning numbers',
              )}
            </>
          )}
        </span>
      </button>

      {/* Expanded winning number groups */}
      {expanded && (
        <div
          className="mt-4 flex flex-col gap-5"
          role="region"
          aria-label={t('setLottery.showNumbers', 'Show Numbers')}
        >
          {standalone.map(renderGroup)}
          {Array.from(rowMap.entries()).map(([rowNum, groups]) => (
            <div key={`row-${rowNum}`} className="grid grid-cols-2 gap-4">
              {groups.map(renderGroup)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default WinningNumbersCollapsible
